import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledReactSelect,
} from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import { ScoreCardTemplateForStage } from 'api-clients/monolith';
import React, { useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { useStyles } from './styles';

interface Option {
  value: number;
  label: string;
}

// The stage's default evaluator, as `ScoreCards::StageEvaluators` derives it.
export type ScorecardAssignee = 'interview_host' | 'opening_owner';

export interface AddScorecardModalProps {
  templates: ScoreCardTemplateForStage[];
  assignee: ScorecardAssignee;
  onAdd: (templateId: number) => void;
  onClose: () => void;
}

export const AddScorecardModal: VFC<AddScorecardModalProps> = ({
  templates,
  assignee,
  onAdd,
  onClose,
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const [selectedId, setSelectedId] = useState<number>();

  const options: Option[] = templates
    .filter(
      (template): template is ScoreCardTemplateForStage & { id: number } =>
        template.id != null,
    )
    .map(template => ({ value: template.id, label: template.name ?? '' }));

  const selectedOption = options.find(option => option.value === selectedId);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (selectedId == null) return;

    onAdd(selectedId);
  };

  return (
    <Modal
      data-testid="add-scorecard-modal"
      ariaLabelledBy="add-scorecard-modal-header"
      open
      onClose={onClose}
      fullScreenOnMobile
      maxWidth={480}
    >
      <form onSubmit={handleSubmit}>
        <ModalHeader
          onClose={onClose}
          ariaLabelledBy="add-scorecard-modal-header"
          showIcon={false}
        >
          <FormattedMessage {...messages.addScorecard} />
        </ModalHeader>
        <ModalContent>
          <StyledReactSelect
            aria-label={intl.formatMessage(messages.scorecard)}
            label={intl.formatMessage(messages.scorecard)}
            placeholder={intl.formatMessage(messages.scorecardPlaceholder)}
            options={options}
            value={selectedOption ?? null}
            onChange={(option: Option) => setSelectedId(option.value)}
            required
          />
          <Typography variant="body2" className={styles.assigneeNote}>
            <FormattedMessage
              {...(assignee === 'interview_host'
                ? messages.assigneeNoteInterviewHost
                : messages.assigneeNoteOpeningOwner)}
            />
          </Typography>
        </ModalContent>
        <ModalFooter>
          <Button type="secondary" onClick={onClose}>
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button
            data-testid="add-scorecard-modal-submit-button"
            submit
            disabled={selectedId == null}
          >
            <FormattedMessage {...messages.addScorecard} />
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
